import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  Warning,
  Error,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  CheckCircle,
  Print as PrintIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { deleteSale, fetchCreditSales, payRemainingAmount } from "../../redux/actions/sales";
import { toast } from "react-toastify";
import moment from "moment";
import { fetchAllShops, selectShop } from "../../redux/actions/shop";
import ProductDetailsDialog from "../../components/ProductDetailsDialog";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Delete as DeleteIcon } from "@mui/icons-material";
import { generateReceipt } from "../../utils/receiptGenerator";
import { useInvoiceNumber } from "../../hooks/useInvoiceNumber";

const VenteParCredit = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { creditSales, loading } = useSelector((state) => state.sales);
  const { shops, selectedShop } = useSelector((state) => state.shop);

  const [openPayment, setOpenPayment] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [paymentData, setPaymentData] = useState({ amount: '', payment_method: "cash" });
  const [openHistory, setOpenHistory] = useState(false);
  const [openProductDetails, setOpenProductDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const { getInvoiceNumber } = useInvoiceNumber();

  // Filter shops to display only shops with enum/type 'electronics'
  const electronicShops = shops.filter(shop => shop.enum === 'electronics' || shop.type === 'electronics');

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (!user) return;

        if (user.role === 'superadmin') {
          await dispatch(fetchAllShops());
          if (shops.length > 0) {
            const firstShop = electronicShops[0];
            if (firstShop) {
              dispatch(selectShop(firstShop));
              await dispatch(fetchCreditSales(firstShop._id));
            }
          }
        } else if (user.shop) {
          const shopId = typeof user.shop === 'object' ? user.shop._id : user.shop;
          if (shopId) {
            await dispatch(fetchCreditSales(shopId));
          }
        }
      } catch (error) {
        console.error('Error loading credit sales:', error);
        toast.error('Failed to load credit sales');
      }
    };

    loadInitialData();
  }, [dispatch, user, shops.length]);

  const handleShopChange = (event) => {
    const shopId = event.target.value;
    if (shopId === 'all') {
      dispatch(selectShop(null));
    } else {
      const shop = shops.find(s => s._id === shopId);
      dispatch(selectShop(shop));
    }
    dispatch(fetchCreditSales(shopId === 'all' ? null : shopId));
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filterSalesByDate = (sales) => {
    return sales.filter((sale) => {
      if (!startDate && !endDate) return true;

      const saleDate = moment(sale.createdAt).startOf('day');

      if (startDate && !endDate) {
        return saleDate.isSameOrAfter(moment(startDate).startOf('day'));
      }

      if (!startDate && endDate) {
        return saleDate.isSameOrBefore(moment(endDate).startOf('day'));
      }

      return saleDate.isBetween(
        moment(startDate).startOf('day'),
        moment(endDate).startOf('day'),
        'day',
        '[]'
      );
    });
  };

  const filterSales = (sales) => {
    const filteredBySearch = sales.filter((sale) =>
      sale.client_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return filterSalesByDate(filteredBySearch);
  };

  const handlePayment = async () => {
    try {
      if (!paymentData.amount || paymentData.amount <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }

      if (paymentData.amount > selectedSale.remaining_amount) {
        toast.error("Payment amount cannot exceed remaining amount");
        return;
      }

      const shopId = user?.role === 'superadmin'
        ? selectedShop?._id
        : (typeof user?.shop === 'object' ? user.shop._id : user?.shop);

      if (!shopId) {
        toast.error("Shop information is missing");
        return;
      }

      await dispatch(payRemainingAmount(selectedSale._id, {
        ...paymentData,
        shop_id: shopId,
        user: user._id
      }));

      await dispatch(fetchCreditSales(shopId));

      toast.success("Paiement traité avec succès");
      setOpenPayment(false);
      setPaymentData({ amount: 0, payment_method: "cash" });
      setSelectedSale(null);
    } catch (error) {
      toast.error(error.message || "Failed to process payment");
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "partial":
        return <Chip icon={<Warning />} label="Partial" color="warning" size="small" />;
      case "unpaid":
        return <Chip icon={<Error />} label="Unpaid" color="error" size="small" />;
      case "paid":
        return <Chip icon={<CheckCircle />} label="Paid" color="success" size="small" />;
      default:
        return null;
    }
  };

  useEffect(() => {
    return () => {
      setSelectedSale(null);
      setPaymentData({ amount: 0, payment_method: "cash" });
      setOpenPayment(false);
      setOpenHistory(false);
      setOpenProductDetails(false);
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" m="20px">
        <Typography>Loading sales...</Typography>
      </Box>
    );
  }

  const handleDelete = async (saleId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette vente ?")) {
      try {
        await dispatch(deleteSale(saleId));
        toast.success("Vente supprimée avec succès");
        // Refresh the sales list
        if (user?.role === 'superadmin') {
          dispatch(fetchCreditSales(selectedShop._id));
        } else {
          dispatch(fetchCreditSales(user.shop._id));
        }
      } catch (error) {
        toast.error(error.message || "Erreur lors de la suppression");
      }
    }
  };

  const calculateTotalSales = () => {
    const filteredSales = filterSales(creditSales);
    return filteredSales.reduce((total, sale) => total + sale.total_amount, 0) || 0;
  };

  const calculateTotalPaid = () => {
    const filteredSales = filterSales(creditSales);
    return filteredSales.reduce((total, sale) => total + sale.amount_paid, 0) || 0;
  };

  const calculateTotalRemaining = () => {
    const filteredSales = filterSales(creditSales);
    return filteredSales.reduce((total, sale) => total + sale.remaining_amount, 0) || 0;
  };

  const handlePrint = async (sale) => {
    try {
      const shopId = sale.shop?._id || sale.shop;
      const invoiceNumber = await getInvoiceNumber(shopId);

      const doc = await generateReceipt(sale, invoiceNumber);
      doc.save(`facture-${invoiceNumber}.pdf`);
    } catch (error) {
      toast.error("Erreur lors de l'impression de la facture");
      console.error('Print error:', error);
    }
  };

  return (
    <Box m="20px">

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4, mt: 1 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total des ventes à crédit
              </Typography>
              <Typography variant="h4">
                {calculateTotalSales().toLocaleString()} Fbu
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main" gutterBottom>
                Total payé
              </Typography>
              <Typography variant="h4" color="success.main">
                {calculateTotalPaid().toLocaleString()} Fbu
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main" gutterBottom>
                Total restant
              </Typography>
              <Typography variant="h4" color="error.main">
                {calculateTotalRemaining().toLocaleString()} Fbu
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Credit Sales ({creditSales?.length || 0})
        </Typography>

        {user.role === 'superadmin' && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Shop</InputLabel>
            <Select
              value={selectedShop ? selectedShop._id : 'all'}
              onChange={handleShopChange}
              label="Shop"
            >
              <MenuItem value="all">All Shops</MenuItem>
              {electronicShops.map((shop) => (
                <MenuItem key={shop._id} value={shop._id}>{shop.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <TextField
          placeholder="Search by Client"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ flexGrow: 1 }}
        />

        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Montant Total</TableCell>
              <TableCell>Montant payé</TableCell>
              <TableCell>Montant restant</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>Effectue par</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filterSales(creditSales).length > 0 ? (
              filterSales(creditSales).map((sale) => (
                <TableRow key={sale._id}>
                  <TableCell>
                    {moment(sale.createdAt).format("DD/MM/YYYY HH:mm")}
                  </TableCell>
                  <TableCell>{sale.client_name}</TableCell>
                  <TableCell>{sale.total_amount?.toLocaleString()} Fbu</TableCell>
                  <TableCell>{sale.amount_paid?.toLocaleString()} Fbu</TableCell>
                  <TableCell>{sale.remaining_amount?.toLocaleString()} Fbu</TableCell>
                  <TableCell>{getStatusChip(sale.payment_status)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Make Payment">
                        <IconButton
                          onClick={() => {
                            setSelectedSale(sale);
                            setOpenPayment(true);
                          }}
                        >
                          <PaymentIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Payment History">
                        <IconButton
                          onClick={() => {
                            setSelectedSale(sale);
                            setOpenHistory(true);
                          }}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() => {
                            setSelectedSale(sale);
                            setOpenProductDetails(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        {/* <Tooltip title="Imprimer Facture">
                          <IconButton onClick={() => handlePrint(sale)}>
                            <PrintIcon />
                          </IconButton>
                        </Tooltip> */}
                      </Tooltip>
                      {user.role === 'superadmin' && (
                        <Tooltip title="Supprimer">
                          <IconButton
                            onClick={() => handleDelete(sale._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{sale.user?.name}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No matching credit sales found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ProductDetailsDialog
        open={openProductDetails}
        onClose={() => {
          setOpenProductDetails(false);
          setSelectedSale(null);
        }}
        products={selectedSale?.products || []}
      />

      {/* Payment Dialog */}
      <Dialog
        open={openPayment}
        onClose={() => {
          setOpenPayment(false);
          setPaymentData({ amount: 0, payment_method: "cash" });
        }}
      >
        <DialogTitle>Effectuer un paiement</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Montant"
              type="number"
              fullWidth
              value={paymentData.amount}
              onChange={(e) => setPaymentData({
                ...paymentData,
                amount: e.target.value
              })}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Mode de paiement"
              select
              fullWidth
              value={paymentData.payment_method}
              onChange={(e) => setPaymentData({
                ...paymentData,
                payment_method: e.target.value
              })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPayment(false)}>Cancel</Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            disabled={!paymentData.amount || paymentData.amount <= 0}
          >
            Payer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        maxWidth="md"
      >
        <DialogTitle>Payment History</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Processed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSale?.payment_history?.map((payment, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {moment(payment.payment_date).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>{payment.amount?.toLocaleString()} Fbu</TableCell>
                    <TableCell>
                      {payment.payment_method === 'debt'
                        ? 'Avance avec dette'
                        : payment.payment_method}
                    </TableCell>
                    <TableCell>{payment.user?.name || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VenteParCredit;
